import React, { useEffect, useState, useMemo } from "react";
import { Navigate, Link } from "react-router-dom";
import { LogOut, FileText, MessageSquare, TrendingUp, ExternalLink, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { LOGO_URL } from "@/constants/site";
import { ADMIN } from "@/constants/testIds";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="border border-[var(--text)]/10 bg-[var(--surface)] p-6 md:p-8">
      <div className="flex items-center justify-between">
        <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text)]/50">
          {label}
        </span>
        <Icon size={16} className="text-[var(--accent)]" />
      </div>
      <div className="mt-4 font-editorial text-4xl md:text-5xl tracking-tighter">
        {value}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

// Small pill that surfaces the Fit Finder quiz recommendation on a
// proposal row. Renders nothing when the lead didn't take the quiz.
function FitChip({ quiz }) {
  if (!quiz || !quiz.chapter) return null;
  return (
    <span
      data-testid="admin-fit-chip"
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-gradient-to-r from-cyan-500/15 to-sky-500/15 border border-cyan-500/30 text-cyan-700 font-mono-brand text-[9.5px] uppercase tracking-[0.2em] whitespace-nowrap"
      title="Recommended fit from the Find-Your-Fit quiz"
    >
      <Sparkles size={10} />
      {quiz.chapter}
    </span>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [stats, setStats] = useState({ contacts: 0, proposals: 0, new_proposals: 0 });
  const [busy, setBusy] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (loading || !user) return;
    const load = async () => {
      setBusy(true);
      try {
        const [c, p, s] = await Promise.all([
          api.get("/contact"),
          api.get("/proposals"),
          api.get("/admin/stats"),
        ]);
        setContacts(c.data);
        setProposals(p.data);
        setStats(s.data);
      } finally {
        setBusy(false);
      }
    };
    load();
  }, [user, loading]);

  const conversionRate = useMemo(() => {
    const total = stats.contacts + stats.proposals;
    if (!total) return "0%";
    return `${Math.round((stats.proposals / total) * 100)}%`;
  }, [stats]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-[100svh] bg-[var(--bg)] text-[var(--text)]">
      {/* Top bar */}
      <header className="border-b border-[var(--text)]/10 bg-[var(--surface-alt)] sticky top-0 z-40 backdrop-blur-md">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Magee" className="h-11 md:h-12" />
            <span className="hidden md:inline font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text)]/40 border-l border-[var(--text)]/10 pl-4">
              Agency Console
            </span>
          </div>
          <div className="flex items-center gap-5">
            <Link
              to="/"
              className="hidden md:inline-flex items-center gap-2 text-[var(--text)]/60 hover:text-[var(--text)] font-mono-brand text-[10px] uppercase tracking-[0.24em]"
            >
              View site <ExternalLink size={12} />
            </Link>
            <span className="hidden md:inline text-[var(--text)]/50 text-sm">{user.email}</span>
            <button
              onClick={logout}
              data-testid={ADMIN.logout}
              className="inline-flex items-center gap-2 border border-[var(--text)]/20 hover:border-[var(--accent)] hover:bg-[var(--accent)] transition-all px-4 py-2 font-mono-brand text-[10px] uppercase tracking-[0.22em]"
            >
              <LogOut size={12} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="mb-10">
          <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-[var(--accent)]">
            § Overview
          </span>
          <h1 className="mt-4 font-editorial text-5xl md:text-7xl tracking-tighter leading-[0.95]">
            Inbound.
          </h1>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
          <StatCard label="Contacts" value={stats.contacts} icon={MessageSquare} />
          <StatCard label="Proposals" value={stats.proposals} icon={FileText} />
          <StatCard label="New Proposals" value={stats.new_proposals} icon={TrendingUp} />
          <StatCard label="Proposal Ratio" value={conversionRate} icon={TrendingUp} />
        </div>

        <Tabs defaultValue="proposals" className="w-full">
          <TabsList className="bg-transparent border-b border-[var(--text)]/10 rounded-none w-full justify-start h-auto p-0 gap-8">
            <TabsTrigger
              value="proposals"
              data-testid={ADMIN.tabProposals}
              className="data-[state=active]:bg-transparent data-[state=active]:text-[var(--text)] data-[state=active]:border-b-2 data-[state=active]:border-[var(--accent)] data-[state=active]:shadow-none rounded-none pb-4 pt-0 px-0 font-mono-brand text-[11px] uppercase tracking-[0.22em] text-[var(--text)]/50"
            >
              Proposals ({proposals.length})
            </TabsTrigger>
            <TabsTrigger
              value="contacts"
              data-testid={ADMIN.tabContacts}
              className="data-[state=active]:bg-transparent data-[state=active]:text-[var(--text)] data-[state=active]:border-b-2 data-[state=active]:border-[var(--accent)] data-[state=active]:shadow-none rounded-none pb-4 pt-0 px-0 font-mono-brand text-[11px] uppercase tracking-[0.22em] text-[var(--text)]/50"
            >
              Contacts ({contacts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="proposals" className="mt-10">
            <div
              data-testid={ADMIN.proposalsTable}
              className="border border-[var(--text)]/10 divide-y divide-[var(--text)]/10"
            >
              <div className="grid grid-cols-12 gap-4 px-4 md:px-6 py-4 bg-[var(--surface-alt)] font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/50">
                <div className="col-span-3">Name / Email</div>
                <div className="col-span-2">Event</div>
                <div className="col-span-1">Attendees</div>
                <div className="col-span-2">Services</div>
                <div className="col-span-2">Fit Finder</div>
                <div className="col-span-2 text-right">Date</div>
              </div>
              {busy && <div className="p-8 text-[var(--text)]/60 text-sm">Loading…</div>}
              {!busy && proposals.length === 0 && (
                <div className="p-8 text-[var(--text)]/60 text-sm">No proposals yet.</div>
              )}
              {proposals.map((p) => (
                <div key={p.id} data-testid={ADMIN.row(p.id)}>
                  <button
                    onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                    className="grid grid-cols-12 gap-4 px-4 md:px-6 py-5 w-full text-left hover:bg-[var(--text)]/[0.04] transition-colors"
                  >
                    <div className="col-span-3">
                      <div className="text-[var(--text)]">{p.name}</div>
                      <div className="text-[var(--text)]/50 text-sm">{p.email}</div>
                    </div>
                    <div className="col-span-2 text-[var(--text)]/80">{p.event_type}</div>
                    <div className="col-span-1 text-[var(--text)]/80">{p.attendees}</div>
                    <div className="col-span-2 text-[var(--text)]/60 text-sm">
                      {p.services?.slice(0, 3).join(", ")}
                      {p.services?.length > 3 ? ` +${p.services.length - 3}` : ""}
                    </div>
                    <div className="col-span-2">
                      {p.quiz ? (
                        <FitChip quiz={p.quiz} />
                      ) : (
                        <span className="text-[var(--text)]/30 text-sm">—</span>
                      )}
                    </div>
                    <div className="col-span-2 text-right text-[var(--text)]/50 text-sm">
                      {formatDate(p.created_at)}
                    </div>
                  </button>
                  {expanded === p.id && (
                    <div className="px-6 py-6 bg-[var(--surface-alt)] border-t border-[var(--text)]/10 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                      <div>
                        <div className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/40 mb-2">
                          Contact
                        </div>
                        <div>{p.company || "—"}</div>
                        <div>{p.role || "—"}</div>
                        <div>{p.phone || "—"}</div>
                      </div>
                      <div>
                        <div className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/40 mb-2">
                          Event
                        </div>
                        <div>{p.location || "—"}</div>
                        <div>{p.event_date || "—"}</div>
                        <div>{p.budget || "—"}</div>
                      </div>
                      <div>
                        <div className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/40 mb-2">
                          Notes
                        </div>
                        <div className="text-[var(--text)]/70 whitespace-pre-wrap">
                          {p.notes || "—"}
                        </div>
                      </div>
                      {p.quiz && (
                        <div className="md:col-span-3 pt-4 mt-2 border-t border-[var(--text)]/10">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/40">
                              Fit Finder · Quiz answers
                            </div>
                            <FitChip quiz={p.quiz} />
                          </div>
                          <div
                            data-testid="admin-quiz-detail"
                            className="grid grid-cols-2 md:grid-cols-4 gap-4"
                          >
                            {[
                              ["Event type", p.quiz.event_type],
                              ["Attendees", p.quiz.attendees],
                              ["Format", p.quiz.format],
                              ["Budget", p.quiz.budget],
                            ].map(([label, val]) => (
                              <div key={label}>
                                <div className="font-mono-brand text-[9px] uppercase tracking-[0.22em] text-[var(--text)]/40 mb-1">
                                  {label}
                                </div>
                                <div className="text-[var(--text)]/80 text-sm">
                                  {val || "—"}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="contacts" className="mt-10">
            <div
              data-testid={ADMIN.contactsTable}
              className="border border-[var(--text)]/10 divide-y divide-[var(--text)]/10"
            >
              <div className="grid grid-cols-12 gap-4 px-4 md:px-6 py-4 bg-[var(--surface-alt)] font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/50">
                <div className="col-span-3">Name / Email</div>
                <div className="col-span-2">Company</div>
                <div className="col-span-5">Message</div>
                <div className="col-span-2 text-right">Date</div>
              </div>
              {busy && <div className="p-8 text-[var(--text)]/60 text-sm">Loading…</div>}
              {!busy && contacts.length === 0 && (
                <div className="p-8 text-[var(--text)]/60 text-sm">No contacts yet.</div>
              )}
              {contacts.map((c) => (
                <div
                  key={c.id}
                  data-testid={ADMIN.row(c.id)}
                  className="grid grid-cols-12 gap-4 px-4 md:px-6 py-5 hover:bg-[var(--text)]/[0.04]"
                >
                  <div className="col-span-3">
                    <div className="text-[var(--text)]">{c.name}</div>
                    <div className="text-[var(--text)]/50 text-sm">{c.email}</div>
                  </div>
                  <div className="col-span-2 text-[var(--text)]/80">{c.company || "—"}</div>
                  <div className="col-span-5 text-[var(--text)]/60 text-sm line-clamp-2">
                    {c.message}
                  </div>
                  <div className="col-span-2 text-right text-[var(--text)]/50 text-sm">
                    {formatDate(c.created_at)}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
